import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';

import {FormInput} from 'src/components/input';
import {ProfileMain} from 'src/components/profile-tab-structure';
import {ACCESS_RIGHTS, useAdminAccessRight} from 'src/hooks/access-rights';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Group, Item} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';

import {MCP_CREDENTIAL_ERROR_MESSAGE, isValidMcpCredential} from '../../constants';
import {selectMcpSaving} from '../../services/mcp-slice';

type Props = {
    mcpClientId: string | null,
    loading: boolean,
    onSave: (clientId: string) => Promise<void>,
};

export const SetupView = ({mcpClientId, loading, onSave}: Props) => {
    const [editedClientId, setEditedClientId] = useState('');
    const [hasBlurred, setHasBlurred] = useState(false);
    const {hasAccess} = useAdminAccessRight();
    const saving = useSelector(selectMcpSaving);

    const label = 'Client ID';
    const placeholder = 'Enter Client ID';

    useEffect(() => {
        setEditedClientId(mcpClientId ?? '');
        setHasBlurred(false);
    }, [mcpClientId]);

    const isChanged = editedClientId !== (mcpClientId ?? '');
    const isValueValid = !editedClientId || isValidMcpCredential(editedClientId);
    const showValidationError = hasBlurred && !!editedClientId && !isValueValid;
    const showValidationSuccess = hasBlurred && !!editedClientId && isValueValid;
    const validationState = showValidationError
        ? false
        : showValidationSuccess || undefined;

    const handleChange = useCallback((value: string) => {
        setEditedClientId(value);
        setHasBlurred(false);
    }, []);

    const {mcpAccessTgl} = useEventTracking();

    const handleSave = useCallback(async () => {
        await onSave(editedClientId);
        mcpAccessTgl({enabled: !!editedClientId});
    }, [editedClientId, mcpAccessTgl, onSave]);

    const handleCancel = useCallback(() => {
        setEditedClientId(mcpClientId ?? '');
        setHasBlurred(false);
    }, [mcpClientId]);

    const editDisabled = useMemo(
        () => !hasAccess(ACCESS_RIGHTS.ACCOUNT_DD_MCP_EDIT),
        [hasAccess],
    );

    if (loading) {
        return null;
    }

    return (
        <ProfileMain header={(
            <Item grow={1}>
                <Group justify="space-between">
                    <Typography
                        fontWeight="semi-bold"
                        variant="heading-level-3">
                        Direct Data MCP Credentials
                    </Typography>
                    {!editDisabled && (
                        <Group>
                            <Button
                                variant="Type.SECONDARY"
                                disabled={!isChanged}
                                onClick={handleCancel}>
                                Discard Changes
                            </Button>
                            <Button
                                busy={saving}
                                variant="Type.PRIMARY"
                                disabled={!isChanged || !isValueValid}
                                onClick={handleSave}>
                                Save Changes
                            </Button>
                        </Group>
                    )}
                </Group>
            </Item>
        )}>
            <Group gap="xs" direction="v">
                <Group direction="v" gap="l" style={{width: 400}}>
                    <FormInput
                        label={label}
                        readOnly={false}
                        disabled={editDisabled}
                        onChange={handleChange}
                        placeholder={placeholder}
                        value={editedClientId}
                        maxLength={255}
                        blurNotChanged={true}
                        onBlur={() => setHasBlurred(true)}
                        valid={validationState}
                        errorDescription={
                            showValidationError
                                ? MCP_CREDENTIAL_ERROR_MESSAGE
                                : undefined
                        }
                    />
                </Group>
            </Group>
        </ProfileMain>
    );
};
